import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, Linkedin, Twitter, Mail, X, ExternalLink, ArrowRight, Menu, X as Close, FileText, Code as LucideCode, Users, BrainCircuit } from 'lucide-react';
import { 
  FaPython, FaJs, FaReact, FaNodeJs, FaDocker, FaAws, FaGitAlt, FaGithub, 
  FaHtml5, FaCss3Alt, FaDatabase, FaServer, FaCloud, FaLock, FaTerminal, FaCode 
} from 'react-icons/fa';
import { 
  SiTypescript, SiTailwindcss, SiNextdotjs, SiTensorflow, SiPytorch, 
  SiScikitlearn, SiMongodb, SiPostgresql, SiExpress, SiVercel, SiRender,
  SiHuggingface, SiLangchain, SiRedis, SiGraphql, SiNumpy, SiPandas,
  SiOpencv, SiCplusplus, SiC, SiGo, SiRust, SiSwift, SiKotlin
} from 'react-icons/si';
import { personalInfo, projects, experience, skills, certifications, metrics } from './mock';

const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const iconMap = {
    FaPython, FaJs, FaReact, FaNodeJs, FaDocker, FaAws, FaGitAlt, FaGithub,
    FaHtml5, FaCss3Alt, FaDatabase, FaServer, FaCloud, FaLock, FaTerminal, FaCode,
    SiTypescript, SiTailwindcss, SiNextdotjs, SiTensorflow, SiPytorch,
    SiScikitlearn, SiMongodb, SiPostgresql, SiExpress, SiVercel, SiRender,
    SiOpenai: BrainCircuit, SiHuggingface, SiLangchain, SiRedis, SiGraphql, SiNumpy, SiPandas,
    SiOpencv, SiCplusplus, SiC, SiJava: LucideCode, SiGo, SiRust, SiSwift, SiKotlin
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent size={16} /> : <LucideCode size={16} />;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { label: 'Work', id: 'work' },
    { label: 'Experience', id: 'experience' },
    { label: 'Research', id: 'research' },
    { label: 'Stack', id: 'stack' },
    { label: 'Proof', id: 'proof' },
    { label: 'About', id: 'about' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#171717]">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[rgba(244,241,234,0.95)] border-b border-[#E5E0D8] backdrop-blur-sm' : 'bg-[#F4F1EA]'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 py-4 md:px-8">
          <div className="flex items-center justify-between">
            <div 
              className="text-xl md:text-2xl font-bold cursor-pointer tracking-tight"
              onClick={() => scrollToSection('hero')}
            >
              VIRAJ
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <div className="flex gap-6 items-center">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm font-medium text-[#171717] uppercase tracking-wider hover:text-[#9D8CFF] transition-colors duration-200 bg-transparent border-none cursor-pointer"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden bg-transparent border-none cursor-pointer p-2"
            >
              {mobileMenuOpen ? <Close size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-[#F4F1EA] border-b border-[#E5E0D8] px-8 py-4 md:hidden"
            >
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-base font-medium text-[#171717] text-left p-2 bg-transparent border-none cursor-pointer"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="min-h-[80vh] flex items-center py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1400px] mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 lg:grid-cols-2 gap-4 md:gap-12 lg:items-center items-center lg:place-items-center place-items-center"
          >
            <div className="md:order-1 order-1 md:text-left text-center">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4"
              >
                SOFTWARE DEVELOPER · AI/ML · GENAI
              </motion.div>

              <h1 className="text-xl md:text-4xl font-bold tracking-tight leading-tight mb-4 text-[#171717]">
                I build things
                <br />
                that actually ship.
              </h1>

              <p className="text-xs md:text-base leading-relaxed text-[#171717] mb-6 opacity-80">
                Software developer focused on AI/ML, Generative AI and full-stack development.
              </p>

              <div className="flex gap-2 md:gap-4 flex-wrap md:justify-start justify-center">
                <button
                  onClick={() => scrollToSection('work')}
                  className="px-3 py-1.5 md:px-6 md:py-2.5 bg-[#171717] text-[#F4F1EA] border-none rounded-md text-xs md:text-base font-semibold cursor-pointer flex items-center gap-2 transition-all duration-200 hover:bg-[#9D8CFF] hover:-translate-y-0.5"
                >
                  VIEW MY WORK <ArrowRight size={14} />
                </button>

                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-3 py-1.5 md:px-6 md:py-2.5 bg-transparent text-[#171717] border border-[#171717] rounded-md text-xs md:text-base font-semibold cursor-pointer flex items-center gap-2 transition-all duration-200 hover:bg-[#171717] hover:text-[#F4F1EA]"
                >
                  LET'S CONNECT <ArrowRight size={14} />
                </button>
              </div>
            </div>

            {/* Profile Image with separate overlays */}
            <div className="relative md:order-2 order-2">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="relative aspect-[4/5] bg-white border-2 border-[#E5E0D8] rounded-xl overflow-hidden max-w-[400px] md:max-w-xs max-w-24 mx-auto shadow-lg"
              >
                <img
                  src="/images/profile/profile-picture.jpeg"
                  alt="Viraj Gavade"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Mobile text overlays */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex md:hidden flex-wrap gap-1 justify-center mt-2"
              >
                {['AI/ML', 'GENAI', 'FULL STACK', 'SOFTWARE'].map((tag, index) => (
                  <motion.div
                    key={tag}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                    className="px-2 py-1 bg-white border border-[#E5E0D8] rounded-md shadow-sm text-xs font-semibold text-[#171717]"
                  >
                    {tag}
                  </motion.div>
                ))}
              </motion.div>

              {/* Desktop text overlays */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="hidden md:flex flex-col gap-2 absolute -right-8 top-1/2 -translate-y-1/2"
              >
                {['AI/ML', 'GENAI', 'FULL STACK', 'SOFTWARE'].map((tag, index) => (
                  <motion.div
                    key={tag}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                    className="px-4 py-2 bg-white border border-[#E5E0D8] rounded-lg shadow-sm text-sm font-semibold text-[#171717]"
                  >
                    {tag}
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Current Status */}
      <section className="py-8 md:py-12 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[#9D8CFF] mb-2">
                CURRENTLY
              </div>
              <div className="text-base md:text-lg font-semibold mb-1">
                Software Developer Intern
              </div>
              <div className="text-sm opacity-70">
                @ LivingThings.ai
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[#9D8CFF] mb-2">
                RESEARCH
              </div>
              <div className="text-base md:text-lg font-semibold mb-1">
                MCP Security
              </div>
              <div className="text-sm opacity-70">
                College Research
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold tracking-widest uppercase text-[#9D8CFF] mb-2">
                FOCUS
              </div>
              <div className="text-base md:text-lg font-semibold mb-1">
                AI/ML · GenAI · Software Development
              </div>
              <div className="text-sm opacity-70">
                Building & Learning
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GitHub Stats */}
      <section className="py-8 md:py-12 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              GITHUB ACTIVITY
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-8 tracking-tight">
              Building in public.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {[
              { label: 'Repositories', value: metrics.repositories, icon: <Github size={16} /> },
              { label: 'Stars', value: metrics.stars, icon: <Github size={16} /> },
              { label: 'Followers', value: metrics.followers, icon: <Users size={16} /> },
              { label: 'Projects', value: metrics.projects, icon: <Github size={16} /> },
              { label: 'Technologies', value: metrics.technologies, icon: <Github size={16} /> },
              { label: 'Years', value: metrics.years, icon: <Github size={16} /> }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white border border-[#E5E0D8] rounded-lg p-4 md:p-6 text-center hover:border-[#9D8CFF] transition-all duration-300"
              >
                <div className="text-[#9D8CFF] mb-2 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-2xl md:text-3xl font-bold mb-1">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm font-semibold tracking-wider uppercase opacity-70">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Work */}
      <section id="work" className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              SELECTED WORK
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Projects that show what I actually build.
            </h2>
          </motion.div>

          {/* Featured Project */}
          {projects.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 md:mt-12 bg-white border border-[#E5E0D8] rounded-xl p-4 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 items-start"
            >
              <div>
                <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
                  FEATURED PROJECT
                </div>
                <div className="text-2xl md:text-3xl font-bold mb-2 opacity-30">
                  01
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">
                  {projects[0].title}
                </h3>
                <div className="text-sm md:text-base font-semibold uppercase tracking-wider text-[#9D8CFF] mb-6">
                  {projects[0].category}
                </div>
                <p className="text-sm md:text-base leading-relaxed mb-6 opacity-80">
                  {projects[0].description}
                </p>

                <div className="mb-6">
                  <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
                    KEY FEATURES
                  </div>
                  <ul className="list-none p-0 m-0">
                    {projects[0].highlights.map((highlight, index) => (
                      <li key={index} className="mb-1.5 flex items-center gap-2 text-sm md:text-base">
                        <span className="text-[#9D8CFF]">→</span>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
                    TECH STACK
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {projects[0].tech.map((tech, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 bg-[#F4F1EA] border border-[#E5E0D8] rounded text-sm md:text-base font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <a
                    href={projects[0].github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#171717] text-[#F4F1EA] no-connection rounded-md text-sm md:text-base font-semibold flex items-center gap-2 transition-all duration-200 hover:bg-[#9D8CFF]"
                  >
                    <Github size={16} /> GitHub
                  </a>
                  {projects[0].demo && (
                    <a
                      href={projects[0].demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-transparent text-[#171717] no-connection border border-[#171717] rounded-md text-sm md:text-base font-semibold flex items-center gap-2 transition-all duration-200 hover:bg-[#171717] hover:text-[#F4F1EA]"
                    >
                      <ExternalLink size={16} /> Live Demo
                    </a>
                  )}
                </div>
              </div>

              <div>
                <div className="aspect-[16/10] bg-[#F4F1EA] border-2 border-[#E5E0D8] rounded-lg overflow-hidden p-4">
                  <img
                    src={projects[0].image}
                    alt={projects[0].title}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Other Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-12">
            {projects.slice(1).map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#9D8CFF] flex flex-col"
              >
                <div className="w-full h-40 bg-[#F4F1EA] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-contain p-2"
                  />
                </div>

                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  <div className="text-xs font-semibold tracking-wider uppercase text-[#9D8CFF] mb-2">
                    {project.category}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-2">
                    {project.title}
                  </h3>
                  <p className="text-sm leading-relaxed mb-4 opacity-80 flex-1">
                    {project.description}
                  </p>

                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {project.tech.slice(0, 4).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-2 py-1 bg-[#F4F1EA] border border-[#E5E0D8] rounded text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setSelectedProject(project)}
                      className="px-4 py-2 bg-[#171717] text-[#F4F1EA] border-none rounded-md text-xs md:text-sm font-semibold cursor-pointer flex items-center gap-2 transition-all duration-200 hover:bg-[#9D8CFF]"
                    >
                      <FileText size={12} /> See Details
                    </button>
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-transparent text-[#171717] no-connection border border-[#E5E0D8] rounded-md text-xs md:text-sm font-semibold flex items-center gap-2 transition-all duration-200 hover:border-[#171717]"
                    >
                      <Github size={12} />
                    </a>
                    {project.demo && (
                      <a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-transparent text-[#171717] no-connection border border-[#E5E0D8] rounded-md text-xs md:text-sm font-semibold flex items-center gap-2 transition-all duration-200 hover:border-[#171717]"
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className="py-12 md:py-24 px-4 md:px-8 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              EXPERIENCE
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Where I've worked.
            </h2>
          </motion.div>

          <div className="mt-8 md:mt-12">
            {experience.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-[#F4F1EA] border border-[#E5E0D8] rounded-lg p-4 md:p-8 mb-0 md:mb-8 last:mb-0"
              >
                <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-1">
                      {exp.role}
                    </h3>
                    <div className="text-base md:text-lg opacity-80">
                      {exp.company}
                    </div>
                  </div>
                  <div className="text-sm font-semibold tracking-wider uppercase text-[#9D8CFF]">
                    {exp.period}
                  </div>
                </div>

                <ul className="list-none p-0 m-0">
                  {exp.description.map((item, itemIndex) => (
                    <li key={itemIndex} className="mb-2 flex items-start gap-3 leading-relaxed text-sm md:text-base">
                      <span className="text-[#9D8CFF] mt-1">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {exp.tech.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {exp.tech.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2.5 py-1 bg-white border border-[#E5E0D8] rounded text-sm md:text-base font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research */}
      <section id="research" className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              CURRENTLY RESEARCHING
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Academic research focus.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 md:mt-12 bg-white border border-[#E5E0D8] rounded-lg p-4 md:p-8"
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              RESEARCH / 01
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-2">
              MCP Security
            </h3>
            <div className="text-base md:text-lg opacity-80 mb-6">
              College Research
            </div>
            <p className="text-sm md:text-base leading-relaxed mb-8 opacity-80">
              Currently conducting college research focused on security considerations around the Model Context Protocol (MCP).
            </p>

            <div className="flex gap-2 mb-8">
              {['MCP', 'SECURITY', 'RESEARCH'].map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-[#F4F1EA] border border-[#E5E0D8] rounded text-sm md:text-base font-semibold text-[#9D8CFF]"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="p-6 bg-[#F4F1EA] border border-[#E5E0D8] rounded-md">
              <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-2">
                RESEARCH DETAILS
              </div>
              <div className="text-sm opacity-60">
                Coming soon
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tech Stack */}
      <section id="stack" className="py-12 md:py-24 px-4 md:px-8 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              THE STACK
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Technologies I work with.
            </h2>
          </motion.div>

          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {Object.entries(skills).map(([category, skillList], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-[#F4F1EA] border border-[#E5E0D8] rounded-lg p-4 md:p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[#D8C4AE]"><LucideCode size={16} /></span>
                  <h3 className="text-sm md:text-base font-semibold tracking-wider uppercase m-0">
                    {category}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {skillList.map((skill, skillIndex) => (
                    <div
                      key={skillIndex}
                      className="px-2.5 py-1 bg-white border border-[#E5E0D8] rounded text-xs md:text-sm font-medium flex items-center gap-2"
                    >
                      <span className="text-[#9D8CFF]">{getIcon(skill.icon)}</span>
                      {skill.name}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof / Certifications */}
      <section id="proof" className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              PROOF, NOT PROMISES.
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Credentials and evidence.
            </h2>
          </motion.div>

          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                onClick={() => setSelectedCert(cert)}
                className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-[#9D8CFF] flex flex-col"
              >
                <div className="w-full h-32 bg-[#F4F1EA] overflow-hidden">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="w-full h-full object-contain p-2"
                  />
                </div>

                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  <div className="text-xs font-semibold tracking-wider uppercase text-[#9D8CFF] mb-2">
                    {cert.category}
                  </div>
                  <h3 className="text-base md:text-lg font-semibold mb-2 leading-relaxed">
                    {cert.title}
                  </h3>
                  <div className="text-sm md:text-base opacity-80 mb-1">
                    {cert.issuer}
                  </div>
                  <div className="text-xs md:text-sm opacity-60 mb-4">
                    {cert.date}
                  </div>
                  <a
                    href={cert.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 text-[#171717] no-underline text-xs md:text-sm font-semibold transition-colors duration-200 hover:text-[#9D8CFF]"
                  >
                    Verify <ExternalLink size={12} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Build Log */}
      <section className="py-12 md:py-24 px-4 md:px-8 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              BUILD LOG
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-4 tracking-tight">
              Selected things I've built.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 md:mt-12"
          >
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-[#171717] text-[#F4F1EA] no-connection rounded-lg text-base md:text-lg font-semibold transition-all duration-200 hover:bg-[#9D8CFF] hover:-translate-y-0.5"
            >
              <Github size={20} />
              View All on GitHub
              <ExternalLink size={18} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              A LITTLE ABOUT ME.
            </div>
            <h2 className="text-xl md:text-3xl font-bold mb-8 tracking-tight">
              Build. Learn. Research.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                BUILD
              </h3>
              <p className="text-sm md:text-base leading-relaxed opacity-80">
                I enjoy turning technical ideas into working software.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                LEARN
              </h3>
              <p className="text-sm md:text-base leading-relaxed opacity-80">
                I'm continuously exploring AI/ML, GenAI and software engineering.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-xl md:text-2xl font-bold mb-4">
                RESEARCH
              </h3>
              <p className="text-sm md:text-base leading-relaxed opacity-80">
                Currently researching security considerations around MCP.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Currently Exploring */}
      <section className="py-12 md:py-24 px-4 md:px-8 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs md:text-sm font-semibold tracking-widest uppercase text-[#9D8CFF] mb-4">
              CURRENTLY EXPLORING →
            </div>
          </motion.div>

          <div className="mt-6 md:mt-8 flex flex-wrap gap-2 md:gap-4">
            {['MCP SECURITY', 'GENAI', 'AI/ML', 'SOFTWARE ENGINEERING', 'AUTOMATION'].map((topic, index) => (
              <motion.span
                key={topic}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="px-6 py-3 bg-[#F4F1EA] border border-[#E5E0D8] rounded-full text-sm md:text-base font-semibold"
              >
                {topic}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-[1200px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-5xl font-bold mb-6 tracking-tight">
              LET'S BUILD SOMETHING.
            </h2>
            <p className="text-base md:text-xl opacity-80 mb-12 max-w-[600px] mx-auto">
              Have an idea, project or opportunity? Let's talk.
            </p>
          </motion.div>

          <div className="flex gap-2 md:gap-4 justify-center flex-wrap">
            <a
              href={`mailto:${personalInfo.email}`}
              className="px-6 py-3 bg-[#171717] text-[#F4F1EA] no-connection rounded-lg text-base md:text-lg font-semibold flex items-center gap-3 transition-all duration-200 hover:bg-[#9D8CFF] hover:-translate-y-0.5"
            >
              <Mail size={20} /> EMAIL ME
            </a>

            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-transparent text-[#171717] no-connection border border-[#171717] rounded-lg text-base md:text-lg font-semibold flex items-center gap-3 transition-all duration-200 hover:bg-[#171717] hover:text-[#F4F1EA] hover:-translate-y-0.5"
            >
              <Linkedin size={20} /> LINKEDIN
            </a>

            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-transparent text-[#171717] no-connection border border-[#171717] rounded-lg text-base md:text-lg font-semibold flex items-center gap-3 transition-all duration-200 hover:bg-[#171717] hover:text-[#F4F1EA] hover:-translate-y-0.5"
            >
              <Github size={20} /> GITHUB
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 md:py-20 px-4 md:px-8 bg-white border-t border-[#E5E0D8]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-xl md:text-2xl font-bold mb-2">
                VIRAJ GAVADE
              </div>
              <div className="text-sm md:text-base opacity-80 mb-4">
                SOFTWARE DEVELOPER · AI/ML · GENAI
              </div>
              <div className="text-sm opacity-60">
                Building things that actually ship.
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold tracking-wider uppercase mb-4">
                NAVIGATION
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => scrollToSection('work')} className="text-sm opacity-80 text-left bg-transparent border-none cursor-pointer hover:text-[#9D8CFF] transition-colors">
                  Work
                </button>
                <button onClick={() => scrollToSection('experience')} className="text-sm opacity-80 text-left bg-transparent border-none cursor-pointer hover:text-[#9D8CFF] transition-colors">
                  Experience
                </button>
                <button onClick={() => scrollToSection('stack')} className="text-sm opacity-80 text-left bg-transparent border-none cursor-pointer hover:text-[#9D8CFF] transition-colors">
                  Stack
                </button>
                <button onClick={() => scrollToSection('contact')} className="text-sm opacity-80 text-left bg-transparent border-none cursor-pointer hover:text-[#9D8CFF] transition-colors">
                  Contact
                </button>
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold tracking-wider uppercase mb-4">
                CONNECT
              </div>
              <div className="flex gap-4">
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#171717] no-underline transition-colors duration-200 hover:text-[#9D8CFF]"
                >
                  <Github size={20} />
                </a>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#171717] no-underline transition-colors duration-200 hover:text-[#9D8CFF]"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href={personalInfo.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#171717] no-underline transition-colors duration-200 hover:text-[#9D8CFF]"
                >
                  <Twitter size={20} />
                </a>
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="text-[#171717] no-underline transition-colors duration-200 hover:text-[#9D8CFF]"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#E5E0D8] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm opacity-60">
              © 2026 Viraj Gavade
            </div>
            <div className="text-sm opacity-60">
              Built with curiosity and code
            </div>
          </div>
        </div>
      </footer>

      {/* Certificate Modal */}
      <AnimatePresence>
        {selectedCert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCert(null)}
            className="fixed top-0 left-0 right-0 bottom-0 bg-black/80 flex items-center justify-center z-50 p-8"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-4 md:p-8 max-w-[700px] w-full max-h-[90vh] overflow-y-auto relative"
            >
              <button
                onClick={() => setSelectedCert(null)}
                className="absolute top-6 right-6 bg-transparent border-none cursor-pointer p-2 rounded-md transition-colors duration-200 hover:bg-[#F4F1EA] z-10"
              >
                <X size={24} />
              </button>

              <div className="aspect-[16/10] bg-[#F4F1EA] rounded-lg overflow-hidden mb-8 p-4">
                <img
                  src={selectedCert.image}
                  alt={selectedCert.title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="text-xs md:text-sm font-semibold tracking-wider uppercase text-[#9D8CFF] mb-3">
                {selectedCert.category}
              </div>
              <h2 className="text-xl md:text-2xl font-bold mb-2">
                {selectedCert.title}
              </h2>
              <div className="text-base md:text-lg opacity-80 mb-8">
                {selectedCert.issuer}
              </div>

              <div className="mb-8">
                <div className="flex justify-between mb-3">
                  <span className="opacity-80">Issued by:</span>
                  <span className="font-semibold">{selectedCert.issuer}</span>
                </div>
                <div className="flex justify-between mb-3">
                  <span className="opacity-80">Category:</span>
                  <span className="font-semibold">{selectedCert.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-80">   Issued:</span>
                  <span className="font-semibold">{selectedCert.date}</span>
                </div>
              </div>

              <a
                href={selectedCert.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 w-full justify-center p-4 bg-[#171717] text-[#F4F1EA] no-connection rounded-lg font-semibold transition-all duration-200 hover:bg-[#9D8CFF] hover:scale-[1.02]"
              >
                Verify Certificate
                <ExternalLink size={18} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
            className="fixed top-0 left-0 right-0 bottom-0 bg-black/80 flex items-center justify-center z-50 p-8"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-4 md:p-8 max-w-[800px] w-full max-h-[90vh] overflow-y-auto relative"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-6 right-6 bg-transparent border-none cursor-pointer p-2 rounded-md transition-colors duration-200 hover:bg-[#F4F1EA] z-10"
              >
                <X size={24} />
              </button>

              <div className="aspect-[16/10] bg-[#F4F1EA] rounded-lg overflow-hidden mb-8 p-4">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="text-xs md:text-sm font-semibold tracking-wider uppercase text-[#9D8CFF] mb-3">
                {selectedProject.category}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {selectedProject.title}
              </h2>
              <div className="text-base md:text-lg opacity-80 mb-8">
                {selectedProject.subtitle}
              </div>

              <div className="mb-8">
                <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
                  ABOUT
                </div>
                <p className="text-base leading-relaxed opacity-80">
                  {selectedProject.longDescription}
                </p>
              </div>

              <div className="mb-8">
                <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
                  KEY FEATURES
                </div>
                <ul className="list-none p-0 m-0">
                  {selectedProject.highlights.map((highlight, index) => (
                    <li key={index} className="mb-2 flex items-center gap-3 text-sm md:text-base">
                      <span className="text-[#9D8CFF]">→</span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <div className="text-xs md:text-sm font-semibold tracking-wider uppercase mb-4">
                  TECH STACK
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tech.map((tech, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-[#F4F1EA] border border-[#E5E0D8] rounded-md text-sm md:text-base font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 flex-wrap">
                <a
                  href={selectedProject.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-[#171717] text-[#F4F1EA] no-connection rounded-lg text-sm md:text-base font-semibold flex items-center gap-2 transition-all duration-200 hover:bg-[#9D8CFF]"
                >
                  <Github size={16} /> GitHub
                </a>
                {selectedProject.demo && (
                  <a
                    href={selectedProject.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-transparent text-[#171717] no-connection border border-[#171717] rounded-lg text-sm md:text-base font-semibold flex items-center gap-2 transition-all duration-200 hover:bg-[#171717] hover:text-[#F4F1EA]"
                  >
                    <ExternalLink size={16} /> Live Demo
                  </a>
                )}
                {selectedProject.docs && (
                  <a
                    href={selectedProject.docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-transparent text-[#1717717] no-connection border border-[#E5E0D8] rounded-lg text-sm md:text-base font-semibold flex items-center gap-2 transition-all duration-200 hover:border-[#171717]"
                  >
                    <FileText size={16} /> Documentation
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;